import { ulid } from "ulid";
import type {
  CreateImportRequest,
  ImportJobApiItem,
  ImportJobStatus,
  PhotoImportPresignResponse,
} from "@recipevault/shared";
import { apiGet, apiPost, apiDelete } from "../../shared/http.js";

export interface ListImportsResult {
  items: ImportJobApiItem[];
  nextCursor: string | null;
}

export async function submitImport(
  req: CreateImportRequest,
  idempotencyKey: string,
): Promise<{ jobId: string; status: ImportJobStatus }> {
  return apiPost<{ jobId: string; status: ImportJobStatus }>("/imports", req, {
    idempotencyKey,
  });
}

/**
 * One-shot URL-import dispatch — mints its own idempotency key (call it once
 * per user tap, never earlier) and returns just the jobId. Shared by every
 * "import this URL" surface (`ShareTarget`, `AddRecipeSheet`'s clipboard
 * option) so the ulid+submitImport pairing lives in exactly one place; each
 * caller still owns its own in-flight guard + navigation.
 */
export async function submitUrlImport(url: string): Promise<string> {
  const { jobId } = await submitImport({ kind: "url", url, model: "haiku" }, ulid());
  return jobId;
}

/**
 * Recipe-less presign for a photo-import upload (Slice D). Returns one photoKey +
 * three presigned PUTs (thumb/full/extract) into staging. No recipe exists yet.
 */
export async function presignPhotoImport(): Promise<PhotoImportPresignResponse> {
  return apiPost<PhotoImportPresignResponse>("/imports/photo-presign", {});
}

export async function listImports(
  qs: { status?: ImportJobStatus; cursor?: string; updatedSince?: string } = {},
): Promise<ListImportsResult> {
  const params = new URLSearchParams();
  if (qs.status) params.set("status", qs.status);
  if (qs.cursor) params.set("cursor", qs.cursor);
  if (qs.updatedSince) params.set("updatedSince", qs.updatedSince);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return apiGet<ListImportsResult>(`/imports${suffix}`);
}

export async function getImport(id: string): Promise<ImportJobApiItem> {
  return apiGet<ImportJobApiItem>(`/imports/${encodeURIComponent(id)}`);
}

export async function deleteImport(id: string): Promise<void> {
  await apiDelete(`/imports/${encodeURIComponent(id)}`);
}
